import {
  CSS_MASK_NONE,
  CSCROLLABLE_MASK_IMAGE_PROPERTY,
  CSCROLLABLE_SCROLL_PROGRESS_BUCKETS,
  CSCROLLABLE_WEBKIT_MASK_IMAGE_PROPERTY,
  SCROLL_DIRECTION,
} from './Cscrollable.constants';
import type { ScrollDirection } from './Cscrollable.types';

export interface ScrollMaskState {
  maskImage: string;
  /** When set, apply as CSS mask-composite (e.g. intersect for dual-axis fades). */
  maskComposite?: string;
  showStartFade: boolean;
  showEndFade: boolean;
  scrollPosition: number;
  scrollSize: number;
  clientSize: number;
}

interface BuildScrollMaskStateInput {
  element: HTMLDivElement;
  direction: ScrollDirection;
  fadeSize: number;
  horizontalFadeSize: number;
  verticalFadeTopSize: number;
  verticalFadeBottomSize: number;
  verticalFadeMiddleOpacity: number;
}

interface ScrollProgressInput {
  scrollPosition: number;
  scrollSize: number;
  clientSize: number;
}

interface BuildVerticalScrollMaskStateInput {
  element: HTMLDivElement;
  verticalFadeTopSize: number;
  verticalFadeBottomSize: number;
  verticalFadeMiddleOpacity: number;
}

interface BuildHorizontalScrollMaskStateInput {
  element: HTMLDivElement;
  fadeSize: number;
}

function buildMasklessScrollState({
  scrollPosition,
  scrollSize,
  clientSize,
}: ScrollProgressInput): ScrollMaskState {
  const maxScroll = Math.max(0, scrollSize - clientSize);
  return {
    maskImage: CSS_MASK_NONE,
    showStartFade: false,
    showEndFade: false,
    scrollPosition: clamp(scrollPosition, 0, maxScroll),
    scrollSize,
    clientSize,
  };
}

export function getScrollProgressBucket({
  scrollPosition,
  scrollSize,
  clientSize,
}: ScrollProgressInput): number {
  const maxScroll = Math.max(0, scrollSize - clientSize);
  if (maxScroll === 0) {
    return 0;
  }

  return Math.min(
    CSCROLLABLE_SCROLL_PROGRESS_BUCKETS,
    Math.floor((scrollPosition / maxScroll) * CSCROLLABLE_SCROLL_PROGRESS_BUCKETS),
  );
}

export function roundPxValue(value: number): number {
  return Math.round(value * 100) / 100;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getVerticalTopFade({
  topFadeSize,
  verticalFadeMiddleOpacity,
}: {
  topFadeSize: number;
  verticalFadeMiddleOpacity: number;
}): string {
  if (topFadeSize > 0) {
    return `transparent 0px, rgba(0, 0, 0, ${verticalFadeMiddleOpacity}) ${topFadeSize}px`;
  }

  return 'black 0';
}

function getVerticalBottomFade({ bottomFadeSize }: { bottomFadeSize: number }): string {
  if (bottomFadeSize > 0) {
    return `black calc(100% - ${bottomFadeSize}px), transparent 100%`;
  }

  return 'black 100%';
}

function getHorizontalLeftFade({ leftFadeSize }: { leftFadeSize: number }): string {
  if (leftFadeSize > 0) {
    return `transparent 0, black ${leftFadeSize}px`;
  }

  return 'black 0';
}

function getHorizontalRightFade({ rightFadeSize }: { rightFadeSize: number }): string {
  if (rightFadeSize > 0) {
    return `black calc(100% - ${rightFadeSize}px), transparent 100%`;
  }

  return 'black 100%';
}

function buildVerticalScrollMaskState({
  element,
  verticalFadeTopSize,
  verticalFadeBottomSize,
  verticalFadeMiddleOpacity,
}: BuildVerticalScrollMaskStateInput): ScrollMaskState {
  const { scrollTop, scrollHeight, clientHeight } = element;
  if (verticalFadeTopSize <= 0 && verticalFadeBottomSize <= 0) {
    return buildMasklessScrollState({
      scrollPosition: scrollTop,
      scrollSize: scrollHeight,
      clientSize: clientHeight,
    });
  }
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  const scrollPosition = clamp(scrollTop, 0, maxScroll);
  const remainingScroll = maxScroll - scrollPosition;
  const topFadeSize = roundPxValue(clamp(scrollPosition, 0, Math.min(verticalFadeTopSize, clientHeight)));
  const bottomFadeLimit = Math.max(0, clientHeight - topFadeSize);
  const bottomFadeSize = roundPxValue(clamp(
    remainingScroll,
    0,
    Math.min(verticalFadeBottomSize, bottomFadeLimit),
  ));
  const topFade = getVerticalTopFade({ topFadeSize, verticalFadeMiddleOpacity });
  const bottomFade = getVerticalBottomFade({ bottomFadeSize });

  return {
    maskImage: `linear-gradient(to bottom, ${topFade}, ${bottomFade})`,
    showStartFade: topFadeSize > 0,
    showEndFade: bottomFadeSize > 0,
    scrollPosition,
    scrollSize: scrollHeight,
    clientSize: clientHeight,
  };
}

function buildHorizontalScrollMaskState({
  element,
  fadeSize,
}: BuildHorizontalScrollMaskStateInput): ScrollMaskState {
  const { scrollLeft, scrollWidth, clientWidth } = element;
  if (fadeSize <= 0) {
    return buildMasklessScrollState({
      scrollPosition: scrollLeft,
      scrollSize: scrollWidth,
      clientSize: clientWidth,
    });
  }
  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  const scrollPosition = clamp(scrollLeft, 0, maxScroll);
  const remainingScroll = maxScroll - scrollPosition;
  const leftFadeSize = roundPxValue(clamp(scrollPosition, 0, Math.min(fadeSize, clientWidth)));
  const rightFadeLimit = Math.max(0, clientWidth - leftFadeSize);
  const rightFadeSize = roundPxValue(clamp(remainingScroll, 0, Math.min(fadeSize, rightFadeLimit)));
  const leftFade = getHorizontalLeftFade({ leftFadeSize });
  const rightFade = getHorizontalRightFade({ rightFadeSize });

  return {
    maskImage: `linear-gradient(to right, ${leftFade}, ${rightFade})`,
    showStartFade: leftFadeSize > 0,
    showEndFade: rightFadeSize > 0,
    scrollPosition,
    scrollSize: scrollWidth,
    clientSize: clientWidth,
  };
}

export function buildScrollMaskState(input: BuildScrollMaskStateInput): ScrollMaskState {
  const primary =
    input.direction === SCROLL_DIRECTION.Vertical
      ? buildVerticalScrollMaskState(input)
      : buildHorizontalScrollMaskState(input);

  // Optional cross-axis horizontal fade on a vertical scrollport (wide tables).
  if (
    input.direction === SCROLL_DIRECTION.Vertical &&
    input.horizontalFadeSize > 0
  ) {
    const horizontal = buildHorizontalScrollMaskState({
      element: input.element,
      fadeSize: input.horizontalFadeSize,
    });
    if (horizontal.maskImage === CSS_MASK_NONE) {
      return primary;
    }
    if (primary.maskImage === CSS_MASK_NONE) {
      return horizontal;
    }
    return {
      maskImage: `${horizontal.maskImage}, ${primary.maskImage}`,
      maskComposite: 'intersect',
      showStartFade: primary.showStartFade || horizontal.showStartFade,
      showEndFade: primary.showEndFade || horizontal.showEndFade,
      scrollPosition: primary.scrollPosition,
      scrollSize: primary.scrollSize,
      clientSize: primary.clientSize,
    };
  }

  return primary;
}

export function applyScrollMask(
  element: HTMLDivElement,
  maskImage: string,
  maskComposite?: string,
): void {
  element.style.setProperty(CSCROLLABLE_MASK_IMAGE_PROPERTY, maskImage);
  element.style.setProperty(CSCROLLABLE_WEBKIT_MASK_IMAGE_PROPERTY, maskImage);
  if (maskComposite) {
    element.style.setProperty('mask-composite', maskComposite);
    element.style.setProperty('-webkit-mask-composite', 'source-in');
  } else {
    element.style.removeProperty('mask-composite');
    element.style.removeProperty('-webkit-mask-composite');
  }
}
